import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import Button from '../../../../../../components/global/Button';
import EditIcon from '../../../../../../components/icons/EditIcon';
import { IDENTITY_GENDER } from '../../../../../../constants/identity';
import InfoCircleIcon from '../../../../../../components/icons/InfoCircleIcon';
import PlusIcon from '../../../../../../components/icons/PlusIcon';
import TrashIcon from '../../../../../../components/icons/TrashIcon';

const propTypes = {
  className: PropTypes.string,
  identityAlbumFolderTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      album_folder_tag: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        uniform_numbered: PropTypes.bool,
      }),
      jersey_number: PropTypes.string,
      uniform_album_folder_tag_level: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      gender: PropTypes.oneOf(Object.values(IDENTITY_GENDER)),
      levels: PropTypes.shape({
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
          }),
        ),
      }),
    }),
  ),
  sportsTagsDelete: PropTypes.func.isRequired,
  albumFolderId: PropTypes.string.isRequired,
  mainLinkedIdentity: PropTypes.shape({
    identity: PropTypes.shape({
      id: PropTypes.string,
      gender: PropTypes.oneOf(Object.values(IDENTITY_GENDER)),
      name: PropTypes.string,
    }),
  }),
};

const RosterIdentityTagsDisplayBox = ({
  identityAlbumFolderTags = [],
  className,
  tags = [],
  sportsTagsDelete,
  albumFolderId,
  identityId,
  gender,
  onEditSportsTag,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const classes = classNames({
    'roster-identity-tags-container': true,
    'text-center': !identityAlbumFolderTags?.length,
    [className]: !!className,
  });

  const getTagInfo = tag => {
    const fullTag = tags.find(t => t.id === tag.album_folder_tag.id);
    if (!fullTag) return { name: 'N/A', gender: 'N/A', level: 'N/A' };

    const level =
      fullTag.levels?.records.find(
        l => l.id === tag?.uniform_album_folder_tag_level?.id,
      )?.name || 'N/A';

    return {
      name: fullTag.name,
      gender: fullTag.gender,
      level,
    };
  };

  const handleDelete = async tag => {
    setIsProcessing(true);
    setError(null);

    try {
      await sportsTagsDelete(tag.id, {
        identityId,
        albumFolderId,
      });
    } catch (err) {
      setError('Failed to delete tag. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {error && <div className="alert alert-danger mb-2">{error}</div>}
      <div className="d-flex justify-content-between align-items-center mb-1 px-1">
        <span>SPORTS</span>
        {identityAlbumFolderTags.length > 0 && (
          <Button
            btnStyle="link"
            onClick={e => {
              e.preventDefault();
              onEditSportsTag?.(null);
            }}
          >
            ADD SPORT
          </Button>
        )}
      </div>
      {identityAlbumFolderTags.length > 0 ? (
        <div className={classes}>
          <div className="roster-identity-tags-card-container">
            {identityAlbumFolderTags.map((tag, index) => {
              const { name, level } = getTagInfo(tag);
              return (
                <div
                  key={`${tag.album_folder_tag.id}-${index}`}
                  className="roster-identity-tags-card"
                >
                  <div>
                    <span className="roster-identity-tags-card-title">
                      {name}
                    </span>
                  </div>
                  <div className="roster-identity-tags-card-body">
                    <div className="roster-identity-tags-card-item">
                      Level: {level}
                    </div>
                    <div className="roster-identity-tags-card-item">
                      Jersey number: {tag.jersey_number}
                    </div>
                  </div>
                  <div className="roster-identity-tags-card-actions">
                    <span
                      className="roster-identity-tags-card-action"
                      onClick={() => onEditSportsTag?.(tag)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.stopPropagation();
                          event.preventDefault();
                          onEditSportsTag?.(tag);
                        }
                      }}
                      role="button"
                      tabIndex="0"
                    >
                      <EditIcon size="15px" />
                    </span>
                    <span
                      className="roster-identity-tags-card-action"
                      onClick={() => handleDelete(tag)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.stopPropagation();
                          event.preventDefault();
                          handleDelete(tag);
                        }
                      }}
                      role="button"
                      tabIndex="0"
                    >
                      <TrashIcon color="#fe5851" size="15px" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mb-2">
          {!identityAlbumFolderTags.length && (
            <div className="roster-identity-info-empty">
              <Button
                onClick={e => {
                  e.preventDefault();
                  onEditSportsTag(null);
                }}
                disabled={!gender || isProcessing}
                aria-label="Add a sport"
                btnStyle="link"
                className="text-bold"
              >
                <PlusIcon size="16px" color="#0d84fe" />
                Add Sport{' '}
              </Button>
              {!gender && (
                <>
                  {' '}
                  <InfoCircleIcon
                    size="16px"
                    color="#0d84fe"
                    data-for="select-gender-tooltip"
                    data-tip="Please select gender first"
                  />
                  <ReactTooltip id="select-gender-tooltip" />{' '}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

RosterIdentityTagsDisplayBox.propTypes = propTypes;

export default RosterIdentityTagsDisplayBox;
